import {
  UseInfiniteQueryOptions as RqUseInfiniteQueryOptions,
  UseQueryOptions as RqUseQueryOptions,
  UseMutationOptions,
  UseMutationResult,
  useInfiniteQuery,
  useMutation,
  UseMutateAsyncFunction,
  useQuery,
  InfiniteData,
  UseInfiniteQueryResult,
  QueryKey,
  queryOptions,
  skipToken,
  DataTag,
  infiniteQueryOptions,
  UseQueryResult,
  QueryClient
} from '@tanstack/react-query';

import { reportException } from '_lib/utils/sentryService';

import {
  ListResult,
  ListResultWithoutPaging,
  PAGING_PARAM_KEYS,
  PagingParams,
  ProviderListResult,
  isProviderListResult,
  omit
} from './apiTypes.shim';

import { ourQueryClient } from './QueryProvider';

import type { AxiosRequestConfig, AxiosInstance } from 'axios';
import type { SetRequired } from 'type-fest';

export { QueryKey, QueryClient };

export type UseQueryOptions<TResDTO> = Omit<
  RqUseQueryOptions<TResDTO>,
  'queryKey' | 'queryFn' | 'initialData' | 'retry' | 'gcTime' | 'staleTime'
>;

type InfiniteQueryPageParam = { page: number; pageSize: number; continuation: string };

type UseInfiniteQueryOptions<TResDTO> = Omit<
  RqUseInfiniteQueryOptions<
    ListResult<TResDTO>,
    Error,
    InfiniteData<ListResult<TResDTO>>,
    QueryKey,
    InfiniteQueryPageParam | undefined
  >,
  'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam' | 'initialData'
>;

export type ResourceId = string;

type ApiExtraOptions = AxiosRequestConfig['extraOptions'];

type ApiOptions = {
  extraOptions?: ApiExtraOptions;
  disableErrorReporting?: boolean;
};

/**
 * GET requests support query params and extraOptions
 */
interface ApiGetOptions<TParams = unknown> extends ApiOptions {
  params?: TParams;
  extraOptions?: ApiExtraOptions & {
    /**
     * @default 1
     */
    retry?: number;
  };
}

export type SkippableApiDetailOptions = Omit<ApiGetOptions, 'params'> & {
  id?: never;
  skipQuery: boolean;
};

export type UnskippableApiDetailOptions = Omit<ApiGetOptions, 'params'> & {
  id: ResourceId;
};

export type ApiDetailOptions = SkippableApiDetailOptions | UnskippableApiDetailOptions;

type OptionRequireParams = { requireParams: true };
type ListTypeOptions = OptionRequireParams | null;

export type UnpagedListOptions<
  TParams = unknown,
  TListTypeOptions extends ListTypeOptions = null
> = TListTypeOptions extends OptionRequireParams
  ? SetRequired<ApiGetOptions<Omit<TParams, keyof PagingParams>>, 'params'>
  : ApiGetOptions<Omit<TParams, keyof PagingParams>> | undefined;

export type ListOptions<
  TParams = unknown,
  TListTypeOptions extends ListTypeOptions = null
> = TListTypeOptions extends OptionRequireParams
  ? SetRequired<ApiGetOptions<TParams & PagingParams>, 'params'>
  : ApiGetOptions<TParams & PagingParams> | undefined;

/**
 * Returns standardized QueryKey object from Api Request parameter object for react-query hooks
 * NOTE: We do not want any page data inside query key as it is dynamic!
 */
export function getQueryKeyParams<T extends Partial<PagingParams & ApiGetOptions['params']>>(params: T) {
  return omit(params, PAGING_PARAM_KEYS);
}

// TODO: make this generic and move to helpers? 'QueryKey' is an alias for 'readonly unknown []'
export const queryKeysAreEqual = (arr1: QueryKey, arr2: QueryKey) =>
  arr1.length === arr2.length && arr1.every((element, index) => element === arr2[index]);

export type CustomQueryHookResult<TResDTO> = UseQueryResult<TResDTO>;

/**
 * Wrapper for a 'custom' query (i.e. a query with a non-standard url or CRUDL response type).
 * Note that the queryKey and queryFn are explicitly required!
 */
export const useCustomQuery = <TResDTO>(queryConfig: SetRequired<RqUseQueryOptions<TResDTO>, 'queryKey' | 'queryFn'>) =>
  useQuery<TResDTO>(queryConfig);

export type UseCustomMutationOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
> = UseMutationOptions<TData, TError, TVariables, TContext>;

export const useCustomMutation = useMutation;

export type QueryKeyResult<T> = DataTag<QueryKey, T, Error>;

export type UnpagedListHookResult<TResDTO> = UseQueryResult<TResDTO[]>;

export interface UnpagedListResource<TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null> {
  unpagedList: (options: UnpagedListOptions<TParams, TListTypeOptions>) => Promise<ListResultWithoutPaging<TResDTO>>;
  useUnpagedList: (
    options: UnpagedListOptions<TParams, TListTypeOptions>,
    queryConfig?: UseQueryOptions<TResDTO[]>
  ) => UnpagedListHookResult<TResDTO>;
  unpagedListQueryKey: (options: UnpagedListOptions<TParams, TListTypeOptions>) => QueryKeyResult<TResDTO[]>;
}

export type ListHookResult<TResDTO> = UseInfiniteQueryResult<InfiniteData<ListResult<TResDTO>, unknown>, Error>;

export interface ListResource<TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null> {
  list: (options: ListOptions<TParams, TListTypeOptions>) => Promise<ListResult<TResDTO>>;
  useList: (
    options: ListOptions<TParams, TListTypeOptions>,
    queryConfig?: UseInfiniteQueryOptions<TResDTO>
  ) => ListHookResult<TResDTO>;
  listQueryKey: (options: ListOptions<TParams, TListTypeOptions>) => QueryKeyResult<InfiniteData<ListResult<TResDTO>>>;
}

/**
 * @returns coalesced array of all pages for a List query
 */
export const transformListData = <TResDTO>(data: InfiniteData<ListResult<TResDTO>> | undefined) =>
  data?.pages.reduce<TResDTO[]>((acc, page) => {
    if (page && page.results) acc.push(...page.results);
    return acc;
  }, []) || [];

export type GetByIdHookResult<TResDTO> = UseQueryResult<TResDTO>;

type GetByIdHook<TResDTO, TDetailOptions extends ApiDetailOptions = ApiDetailOptions> = (
  options: TDetailOptions,
  /* Force skipQuery for ID queries, do not expose 'enabled' */
  queryConfig?: Omit<UseQueryOptions<TResDTO>, 'enabled'>
) => GetByIdHookResult<TResDTO>;

export interface GetByIdResource<TResDTO, TDetailOptions extends ApiDetailOptions = ApiDetailOptions> {
  getById: (options: UnskippableApiDetailOptions) => Promise<TResDTO>;
  useGetById: GetByIdHook<TResDTO, TDetailOptions>;
  getByIdQueryKey: (options: Pick<UnskippableApiDetailOptions, 'id'>) => QueryKeyResult<TResDTO>;
}

interface MutationVariables {
  options?: ApiOptions;
}

interface MutationDetailVariables extends MutationVariables {
  id: ResourceId;
}

interface CreateMutationVariables<TReqDTO> extends MutationVariables {
  data: TReqDTO;
  addRecordToCache?: boolean;
}

export type CreateHook<TReqDTO, TResDTO> = (
  mutationConfig?: UseMutationOptions<TResDTO, unknown, CreateMutationVariables<TReqDTO>>
) => UseMutationResult<TResDTO, unknown, CreateMutationVariables<TReqDTO>> & {
  create: UseMutateAsyncFunction<TResDTO, unknown, CreateMutationVariables<TReqDTO>>;
};

export interface CreateResource<TReqDTO, TResDTO> {
  useCreate: CreateHook<TReqDTO, TResDTO>;
  create: (data: TReqDTO, options?: ApiOptions) => Promise<TResDTO>;
}

interface PatchMutationVariables<TReqDTO> extends MutationDetailVariables {
  data: TReqDTO;
}

export type PatchHook<TReqDTO, TResDTO> = (
  mutationConfig?: UseMutationOptions<TResDTO, unknown, PatchMutationVariables<TReqDTO>>
) => UseMutationResult<TResDTO, unknown, PatchMutationVariables<TReqDTO>> & {
  patch: UseMutateAsyncFunction<TResDTO, unknown, PatchMutationVariables<TReqDTO>>;
};

export interface PatchResource<TReqDTO, TResDTO> {
  usePatch: PatchHook<TReqDTO, TResDTO>;
  patch: (id: ResourceId, data: TReqDTO, options?: ApiOptions) => Promise<TResDTO>;
}

interface PutMutationVariables<TReqDTO> extends MutationDetailVariables {
  data: TReqDTO;
}

export type PutHook<TReqDTO, TResDTO> = (
  mutationConfig?: UseMutationOptions<TResDTO, unknown, PutMutationVariables<TReqDTO>>
) => UseMutationResult<TResDTO, unknown, PutMutationVariables<TReqDTO>> & {
  put: UseMutateAsyncFunction<TResDTO, unknown, PutMutationVariables<TReqDTO>>;
};

export interface PutResource<TReqDTO, TResDTO> {
  usePut: PutHook<TReqDTO, TResDTO>;
  put: (id: ResourceId, data: TReqDTO, options?: ApiOptions) => Promise<TResDTO>;
}

type DeleteHook<TResDTO = unknown> = (
  mutationConfig?: UseMutationOptions<TResDTO, unknown, MutationDetailVariables>
) => UseMutationResult<TResDTO, unknown, MutationDetailVariables> & {
  deleteItem: UseMutateAsyncFunction<TResDTO, unknown, MutationDetailVariables>;
};

/**
 * @template TResDTO The delete response is intentionally set to unknown as our API will return nothing by default.
 * If a DELETE endpoint returns data for a special case, it should be explicitly typed on that resource.
 */
export interface DeleteResource<TResDTO = unknown> {
  useDelete: DeleteHook<TResDTO>;
  deleteItem: (id: ResourceId, options?: ApiOptions) => Promise<TResDTO>;
}

type ReactivateHook<TResDTO> = (
  mutationConfig?: UseMutationOptions<TResDTO, unknown, MutationDetailVariables>
) => UseMutationResult<TResDTO, unknown, MutationDetailVariables> & {
  reactivate: UseMutateAsyncFunction<TResDTO, unknown, MutationDetailVariables>;
};

export interface ReactivateResource<TResDTO> {
  useReactivate: ReactivateHook<TResDTO>;
  reactivate: (id: ResourceId, options?: ApiOptions) => Promise<TResDTO>;
}

// TODO: discuss handling error reporting here. Note that reporting an error in the queryFn IF we support RQ retires
//       will cause each thrown error to be reported, while our 'internal' retry logic in the API service will only throw once.
export function createApiResource(apiClient: AxiosInstance, resourceUri: string) {
  const unpagedList = async <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: UnpagedListOptions<TParams, TListTypeOptions>
  ): Promise<ListResultWithoutPaging<TResDTO>> => {
    try {
      const res = await apiClient.get(resourceUri, {
        ...options,
        params: { ...options?.params, paging: false },
        extraOptions: { retry: 1, ...options?.extraOptions }
      });
      return res.data;
    } catch (err) {
      if (!options?.disableErrorReporting) reportException(err);
      throw err;
    }
  };

  const unpagedListTaggedQueryOptions = <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: UnpagedListOptions<TParams, TListTypeOptions>
  ) =>
    queryOptions<TResDTO[]>({
      queryKey: [
        resourceUri,
        'UNPAGED_LIST',
        ...(options?.params ? [{ params: getQueryKeyParams(options.params) }] : [])
      ],
      queryFn: async () => {
        const res = await unpagedList<TResDTO>(options);
        return res.results;
      }
    });

  const useUnpagedList = <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: UnpagedListOptions<TParams, TListTypeOptions>,
    queryConfig?: UseQueryOptions<TResDTO[]>
  ) =>
    useQuery<TResDTO[]>({
      ...unpagedListTaggedQueryOptions(options),
      ...queryConfig
    });

  const unpagedListQueryKey = <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: UnpagedListOptions<TParams, TListTypeOptions>
  ) => unpagedListTaggedQueryOptions<TResDTO>(options).queryKey;

  const list = async <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: ListOptions<TParams, TListTypeOptions>
  ): Promise<ListResult<TResDTO>> => {
    // TODO - until we completely remove deprecated `continuation`, we could update code here to transform it to
    //       newer `page` & `pageSize` & maybe `hasMore` so that our public interface is completely unaware of it
    try {
      const res = await apiClient.get(resourceUri, {
        ...options,
        extraOptions: { retry: 1, ...options?.extraOptions }
      });
      return res.data;
    } catch (err) {
      if (!options?.disableErrorReporting) reportException(err);
      throw err;
    }
  };

  const listTaggedQueryOptions = <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: ListOptions<TParams, TListTypeOptions>
  ) =>
    infiniteQueryOptions<
      ListResult<TResDTO>,
      Error,
      InfiniteData<ListResult<TResDTO>>,
      QueryKey,
      InfiniteQueryPageParam | undefined
    >({
      queryKey: [resourceUri, 'LIST', ...(options?.params ? [{ params: getQueryKeyParams(options.params) }] : [])],
      queryFn: async ({ pageParam }) => {
        const params = { ...options?.params, ...(pageParam ? pageParam : {}), paging: true };
        const res = await list<TResDTO>({ params, extraOptions: options?.extraOptions });
        return res;
      },
      initialPageParam: undefined,
      getNextPageParam: (lastPageResult: ListResult<TResDTO> | ProviderListResult<TResDTO>, _pages) => {
        // if purely legacy, intentionally set invalid page/pageSize to ensure continuation-ONLY behavior
        if (lastPageResult.continuation && (!lastPageResult.page || !lastPageResult.pageSize)) {
          return { continuation: `${lastPageResult.continuation}`, page: -1, pageSize: -1 };
        } else if (
          (isProviderListResult(lastPageResult) && lastPageResult.hasMore) ||
          // NOTE - for 'our' list results, if the last page has the EXACT same length as the page size,
          // we will erroneously show the button to load more, at least until we implement a 'hasMore' like ST.
          (!isProviderListResult(lastPageResult) &&
            lastPageResult.pageSize &&
            lastPageResult.results.length === lastPageResult.pageSize)
        ) {
          const lastPage = lastPageResult.page || 1;
          const pageSize = lastPageResult.pageSize || 50;

          return {
            page: lastPage + 1,
            pageSize,
            continuation: `${lastPageResult.continuation || lastPage * pageSize}`
          };
        }

        // Returning undefined should signal to useInfiniteQuery that there are no more pages fetch.
        // Also, the `hasNextPage` hook result property will be set to false
        // for more info, see: https://tanstack.com/query/v5/docs/framework/react/guides/infinite-queries
        return undefined;
      }
    });

  const useList = <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: ListOptions<TParams, TListTypeOptions>,
    queryConfig?: UseInfiniteQueryOptions<TResDTO>
  ) =>
    useInfiniteQuery({
      ...listTaggedQueryOptions<TResDTO>(options),
      ...queryConfig
    });

  const listQueryKey = <TResDTO, TParams = unknown, TListTypeOptions extends ListTypeOptions = null>(
    options: ListOptions<TParams, TListTypeOptions>
  ) => listTaggedQueryOptions<TResDTO>(options).queryKey;

  const getById = async <TResDTO, TDetailOptions extends UnskippableApiDetailOptions = UnskippableApiDetailOptions>(
    options: TDetailOptions
  ) => {
    try {
      const res = await apiClient.get<TResDTO>(`${resourceUri}/${options.id}`, {
        ...options,
        extraOptions: { retry: 1, ...options?.extraOptions }
      });
      return res.data;
    } catch (err) {
      if (!options?.disableErrorReporting) reportException(err);
      throw err;
    }
  };

  const getByIdTaggedQueryOptions = <TResDTO, TDetailOptions extends ApiDetailOptions = ApiDetailOptions>(
    options: TDetailOptions
  ) =>
    queryOptions<TResDTO>({
      queryKey: [resourceUri, options.id],
      queryFn: options.id ? async () => getById(options) : skipToken
    });

  const useGetById = <TResDTO, TDetailOptions extends ApiDetailOptions = ApiDetailOptions>(
    options: TDetailOptions,
    queryConfig?: UseQueryOptions<TResDTO>
  ) =>
    useQuery<TResDTO>({
      ...getByIdTaggedQueryOptions(options),
      ...queryConfig
    });

  const getByIdQueryKey = <TResDTO, TDetailOptions extends UnskippableApiDetailOptions = UnskippableApiDetailOptions>(
    options: Pick<TDetailOptions, 'id'>
  ) => getByIdTaggedQueryOptions<TResDTO>(options).queryKey;

  const create = async <TReqDTO>(data: TReqDTO, options?: ApiOptions) => {
    const response = await apiClient.post(resourceUri, data, options);
    return response.data;
  };

  const useCreate = <TResDTO, TReqDTO>(
    mutationConfig?: UseMutationOptions<TResDTO, unknown, CreateMutationVariables<TReqDTO>>
  ) => {
    const context = useMutation<TResDTO, unknown, CreateMutationVariables<TReqDTO>>({
      mutationFn: async ({ data, options }) => create(data, options),
      onSuccess: (data, { addRecordToCache }) => {
        ourQueryClient.invalidateQueries({ queryKey: [resourceUri], refetchType: 'active' });
        // NOTE: not every resource has an id (see serviceTitan note resource)!
        if (addRecordToCache && data && typeof data === 'object' && 'id' in data && typeof data['id'] === 'string') {
          ourQueryClient.setQueryData(getByIdQueryKey<TResDTO>({ id: data.id }), data);
        }
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });
    return { ...context, create: context.mutateAsync };
  };

  const patch = async <TReqDTO, TResDTO>(id: ResourceId, data: TReqDTO, options?: ApiOptions): Promise<TResDTO> => {
    const response = await apiClient.patch(`${resourceUri}/${id}`, data, options);
    return response.data;
  };

  const usePatch = <TResDTO, TReqDTO>(
    mutationConfig?: UseMutationOptions<TResDTO, unknown, { id: ResourceId; data: TReqDTO; options?: ApiOptions }>
  ) => {
    const context = useMutation<TResDTO, unknown, { id: ResourceId; data: TReqDTO; options?: ApiOptions }>({
      mutationFn: async ({ id, data, options }) => patch(id, data, options),
      onSuccess: (data, { id }) => {
        ourQueryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === resourceUri && !queryKeysAreEqual(query.queryKey, getByIdQueryKey({ id })),
          refetchType: 'active'
        });
        ourQueryClient.setQueryData(getByIdQueryKey<TResDTO>({ id }), data);
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });
    return { ...context, patch: context.mutateAsync };
  };

  const put = async <TReqDTO, TResDTO>(id: ResourceId, data: TReqDTO, options?: ApiOptions): Promise<TResDTO> => {
    const response = await apiClient.put(`${resourceUri}/${id}`, data, options);
    return response.data;
  };

  const usePut = <TResDTO, TReqDTO>(
    mutationConfig?: UseMutationOptions<TResDTO, unknown, { id: ResourceId; data: TReqDTO; options?: ApiOptions }>
  ) => {
    const context = useMutation<TResDTO, unknown, { id: ResourceId; data: TReqDTO; options?: ApiOptions }>({
      mutationFn: async ({ id, data, options }) => put(id, data, options),
      onSuccess: (data, { id }) => {
        ourQueryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === resourceUri && !queryKeysAreEqual(query.queryKey, getByIdQueryKey({ id })),
          refetchType: 'active'
        });
        ourQueryClient.setQueryData(getByIdQueryKey<TResDTO>({ id }), data);
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });
    return { ...context, put: context.mutateAsync };
  };

  const deleteItem = async <TResDTO>(id: ResourceId, options?: ApiOptions): Promise<TResDTO> => {
    const res = await apiClient.delete(`${resourceUri}/${id}`, options);
    return res.data;
  };

  const useDelete = <TResDTO>(mutationConfig?: UseMutationOptions<TResDTO, unknown, MutationDetailVariables>) => {
    const context = useMutation({
      mutationFn: async ({ id, options }) => deleteItem(id, options),
      onSuccess: (_data, { id }) => {
        ourQueryClient.invalidateQueries({ queryKey: [resourceUri], refetchType: 'active' });
        ourQueryClient.removeQueries({ queryKey: getByIdQueryKey({ id }) });
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });
    return { ...context, deleteItem: context.mutateAsync };
  };

  const reactivate = async <TResDTO>(id: ResourceId, options?: ApiOptions): Promise<TResDTO> => {
    const res = await apiClient.patch(`${resourceUri}/${id}/reactivate`, options);
    return res.data;
  };

  const useReactivate = <TResDTO>(mutationConfig?: UseMutationOptions<TResDTO, unknown, MutationDetailVariables>) => {
    const context = useMutation({
      mutationFn: async ({ id, options }) => reactivate(id, options),
      onSuccess: () => {
        ourQueryClient.invalidateQueries({ queryKey: [resourceUri], refetchType: 'active' });
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });
    return { ...context, reactivate: context.mutateAsync };
  };

  return {
    // Direct method behavior
    unpagedList,
    list,
    getById,
    create,
    patch,
    put,
    deleteItem,
    reactivate,

    // Hook behavior
    useUnpagedList,
    useList,
    useGetById,
    useCreate,
    usePatch,
    usePut,
    useDelete,
    useReactivate,

    // Query Keys
    unpagedListQueryKey,
    listQueryKey,
    getByIdQueryKey
  };
}
